import { useParams } from 'react-router-dom';
import CDateUpdatedAtom from '../components/CDateUpdatedAtom';
import { Box, Button, CircularProgress, Stack, Tab, Tabs, useMediaQuery, useTheme } from '@mui/material';
import { DashboardOutlined } from '@mui/icons-material';
import { useState, useEffect, useCallback } from 'react';
import CInvestDisclaimer from '../components/CInvestDisclaimer';

import { useTranslation } from 'react-i18next';

import React from 'react';
import investmentActionLang from '../langs/investmentActionLang';

import useInvestExpertiseKpis from '../hooks/expertise/useInvestExpertiseKpis';
import useInvestFunds from '../hooks/fund/useInvestFunds';
import IInvestExpertiseFilters from '../interfaces/expertise/IInvestExpertiseFilters';
import IInvestExpertiseFiltersResult from '../interfaces/expertise/IInvestExpertiseFiltersResult';
import VInvestFilteredFundsChips from '../views/VInvestFilteredFundsChips';
import {
	useQueryParams,
	StringParam,
	NumberParam,
	DelimitedNumericArrayParam,
	BooleanParam,
	withDefault,
	DelimitedArrayParam,
} from 'use-query-params';

import IFund from '../interfaces/fund/IFund';
import CInfoKpi from '../components/CInfoKpi';
import _ from 'lodash';

import { Helmet } from 'react-helmet';

import IFundType from '../interfaces/expertise/IFundType';
import { useAuthContext } from '../../auth/contexts/AuthContext';
import { useAppSettings } from '../../../app/features/settings/providers/AppSettingsProvider';

import VInvestExpertiseAssets from '../views/expertise/assets/VInvestExpertiseAssets';
import VInvestExpertisePortfolio from '../views/expertise/VInvestExpertisePortfolio';
import VInvestExpertiseHistory from '../views/expertise/VInvestExpertiseHistory';
import VInvestExpertiseSynthesis from '../views/expertise/VInvestExpertiseSynthesis';
import AppError404 from '../../../app/features/error/404/AppError404';
import investmentTypeOngletLang from '../features/fund-type/langs/investmentTypeOngletLang';
import AppCustomIcon from '../../../app/features/icon/AppIcon';
import AppLoaderContent from '../../../app/features/loader/content/AppLoaderContent';
import CAppSwitch from '../../../app/components/CAppSwitch';
import CFilters from '../features/fund-type/components/filters/CFilters';
import CAppTitle from '../../../app/components/CAppTitle';
import useInvestExpertises from '../hooks/expertise/useInvestExpertises';
import { TInvestFundCurrency } from '../types/fund/TInvestFundCurrency';
import { useAppNavigationContext } from '../../../app/features/navigation/providers/AppNavigationProvider';
import FInvestSelectCurrencySwitch from '../features/select-currency/FInvestSelectCurrencySwitch';
import ViewFundByAssets from '../views/expertise/assets/listed-assets/VInvestExpertiseAssetsListedAssets';

const PInvestExpertise = () => {
	const routeParams = useParams();
	const typeTag = routeParams.investExpertiseTag;

	const { t } = useTranslation();
	const authContext = useAuthContext();

	const { realizationStatus: defaultRealizationStatus } = useAppSettings();

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const navigationContext = useAppNavigationContext();

	const [sort, setSort] = useState<'asc' | 'desc'>('desc');

	useEffect(() => {
		if (navigationContext.displayCurrencySelector === false) {
			navigationContext.setDisplayCurrencySelector(true);
		}
	}, [navigationContext]);

	// Liste des fonds de l'utilisateur
	const { funds: allFundsForTypeTag } = useInvestFunds(typeTag);
	const { funds: allFunds } = useInvestFunds(typeTag, navigationContext.currentCurrency);
	const investExpertises = useInvestExpertises();

	useEffect(() => {
		if (navigationContext && allFundsForTypeTag) {
			const currencies = _.uniq(allFundsForTypeTag.map((x) => x.currency));
			if (currencies.length === 1) {
				// Cas où l'on a qu'une currency on force le switch
				if (navigationContext.currentCurrency !== currencies[0]) {
					navigationContext.setCurrentCurrency(currencies[0]);
				}
				navigationContext.setDisplayCurrencySelector(false);
			} else if (currencies.length > 1) {
				// Cas où l'on a plusieurs currency
				navigationContext.setDisplayCurrencySelector(true);
			}
		}
	}, [navigationContext, allFundsForTypeTag]);

	// Filtres
	const [filters, setFilters] = useState<IInvestExpertiseFilters>({
		typeTag: null,
		fundIds: null,
		investorIds: null,
		investmentYear: null,
		reportingPeriod: null,
		domainTag: null,
		vintage: null,
		isRealized: null,
		sfdr: undefined,
		assetRealizationStatusTag: null,
		countryCode: null,
		assetClasses: null,
		thematiques: null,
		labels: null,
		classificationAMFs: null,
		placementDurations: null,
		riskLevels: null,
	});

	// Déclaration du format des query params (url)
	const queryParamsDefaultValue = {
		view: withDefault(StringParam, 'synthesis'),
		portfolioView: withDefault(StringParam, 'subportfolio'),
		fundIds: withDefault(DelimitedNumericArrayParam, null),
		investorIds: withDefault(DelimitedNumericArrayParam, null),
		investmentYear: withDefault(NumberParam, null),
		reportingPeriod: withDefault(StringParam, null),
		domainTag: withDefault(StringParam, null),
		vintage: withDefault(NumberParam, null),
		isRealized: withDefault(BooleanParam, undefined, false),
		sfdr: withDefault(StringParam, undefined, false),
		assetRealizationStatusTag: withDefault(StringParam, null),
		countryCode: withDefault(StringParam, null),
		assetClasses: withDefault(DelimitedArrayParam, null),
		thematiques: withDefault(DelimitedArrayParam, null),
		labels: withDefault(DelimitedArrayParam, null),
		classificationAMFs: withDefault(DelimitedArrayParam, null),
		placementDurations: withDefault(DelimitedArrayParam, null),
		riskLevels: withDefault(DelimitedArrayParam, null),
	};

	const [queryParams, setQueryParams] = useQueryParams(queryParamsDefaultValue);

	// Initialisation des filtres quand on arrive sur la page et que typeTag est null
	useEffect(() => {
		if (queryParams && typeTag && filters.typeTag === null && defaultRealizationStatus) {
			const newFilters = {
				...filters,
				typeTag: typeTag,
				fundIds: queryParams.fundIds,
				investorIds: queryParams.investorIds,
				investmentYear: queryParams.investmentYear,
				reportingPeriod: queryParams.reportingPeriod,
				domainTag: queryParams.domainTag,
				vintage: queryParams.vintage,
				isRealized:
					queryParams.isRealized === undefined
						? defaultRealizationStatus === 'realized'
							? true
							: defaultRealizationStatus === 'unrealized'
							? false
							: null
						: queryParams.isRealized,
				sfdr: queryParams.sfdr,
				assetRealizationStatusTag: queryParams.assetRealizationStatusTag,
				countryCode: queryParams.countryCode,
				assetClasses: queryParams.assetClasses,
				thematiques: queryParams.thematiques,
				labels: queryParams.labels,
				classificationAMFs: queryParams.classificationAMFs,
				placementDurations: queryParams.placementDurations,
				riskLevels: queryParams.riskLevels,
			};

			// `use-query-params` types each array entry as `T | null` (it parses null
			// segments from the URL) but `IInvestExpertiseFilters` doesn't model
			// per-entry nullability. Cast is contained to this single hand-off.
			setFilters(newFilters as IInvestExpertiseFilters);
		}
	}, [queryParams, typeTag, filters, defaultRealizationStatus]);

	// Mise à jour de l'url queryParams quand les filtres changent
	useEffect(() => {
		if (filters && queryParams) {
			const queryParamsTmp = {
				view: queryParams.view,
				portfolioView: queryParams.portfolioView,
				fundIds: filters.fundIds,
				investorIds: filters.investorIds,
				investmentYear: filters.investmentYear,
				reportingPeriod: filters.reportingPeriod,
				domainTag: filters.domainTag,
				vintage: filters.vintage,
				isRealized: filters.isRealized,
				sfdr: filters.sfdr,
				assetRealizationStatusTag: filters.assetRealizationStatusTag,
				countryCode: filters.countryCode,
				assetClasses: filters.assetClasses,
				thematiques: filters.thematiques,
				labels: filters.labels,
				classificationAMFs: filters.classificationAMFs,
				placementDurations: filters.placementDurations,
				riskLevels: filters.riskLevels,
			};
			if (!_.isEqual(queryParams, queryParamsTmp)) {
				setQueryParams(queryParamsTmp);
			}
		}
	}, [filters, queryParams, setQueryParams]);

	// On remonte les données de mapping en fonction du type passé en paramètre.
	// Sentinels: `null` = still loading, `undefined` = expertises loaded but no
	// match for `typeTag` (drives the 404 branch at the bottom of the render).
	const [investExpertise, setInvestExpertise] = useState<IFundType | null | undefined>(null);
	useEffect(() => {
		if (typeTag && investExpertises) {
			setInvestExpertise(investExpertises.find((x) => x.tag === typeTag));
		} else {
			setInvestExpertise(null);
		}
	}, [investExpertises, typeTag]);

	// Résultat après filtrage
	const [resultData, setResultData] = useState<IInvestExpertiseFiltersResult | null>(null);

	const [requestFilters, setRequestFilters] = useState<IInvestExpertiseFilters>({
		typeTag: typeTag ?? null,
		fundIds: null,
		investorIds: null,
		reportingPeriod: null,
	});
	const { results: dataResult, isFetching: isFetchingResult } = useInvestExpertiseKpis(requestFilters);

	useEffect(() => {
		if (dataResult) {
			if (investExpertise?.tag === 'listed_assets') {
				const sortedSgKpis = _.orderBy(dataResult.kpisByFundForSiennaGestion, (x) => Number(x.kpi?.aum), [
					sort,
				]);

				setResultData({
					...dataResult,
					kpisByFundForSiennaGestion: sortedSgKpis,
				});
			} else {
				setResultData(dataResult);
			}
		}
	}, [isFetchingResult, dataResult, sort, investExpertise?.tag]);

	const handleSelectOnglet = (_event: React.SyntheticEvent, newValue: string) => {
		setQueryParams((prevData) => ({
			...prevData,
			view: newValue,
		}));
	};

	const handleRequest = useCallback(
		(filterFunds: IFund[], reportingPeriod: string, investorIds: number[]) => {
			if (investExpertise) {
				setRequestFilters((prevData) => ({
					...prevData,
					typeTag: investExpertise ? investExpertise.tag : null,
					fundIds: filterFunds ? filterFunds.map((x) => x.id) : null,
					investorIds: investorIds,
					reportingPeriod: reportingPeriod,
				}));
			}
		},
		[investExpertise],
	);
	// Bouton export data de funds portfolio
	const [callExportData, setCallExportData] = useState(false);

	// Bouton export data de funds history
	const [callExportDataHistory, setCallExportDataHistory] = useState(false);

	// Bouton export data de assets
	const [callExportDataAssets, setCallExportAssets] = useState(false);

	// Bouton export data des actifs cotes
	const [callExportDataListedAssets, setCallExportListedAssets] = useState(false);

	const handleSwitchFundsSubportfolio = () => {
		let portfolioViewNew = 'funds';
		if (queryParams.portfolioView === 'funds') {
			portfolioViewNew = 'subportfolio';
		}

		setQueryParams((prevParams) => ({ ...prevParams, portfolioView: portfolioViewNew }));
	};

	const [currency, setCurrency] = useState<TInvestFundCurrency | null>(null);
	useEffect(() => {
		if (allFunds && resultData) {
			const currencies = _.uniq(
				allFunds
					.filter(
						(x) =>
							resultData.kpisByFund?.map((k) => k.fund?.id).includes(x.id) ||
							(investExpertise?.tag === 'listed_assets' &&
								resultData.kpisByFundForSiennaGestion?.map((k) => k.fund?.id).includes(x.id)),
					)
					.map((x) => x.currency),
			);
			setCurrency(currencies && currencies.length === 1 ? currencies[0] : null);
		}
	}, [resultData, allFunds, investExpertise?.tag]);

	const renderPanel = () => {
		if (resultData && investExpertise) {
			switch (queryParams.view) {
				case 'assets':
					if (investExpertise.tag === 'listed_assets') {
						return <ViewFundByAssets data={resultData.kpisByFundForSiennaGestion ?? []} />;
					}
					return (
						<VInvestExpertiseAssets
							allFunds={allFunds ?? []}
							resultData={resultData}
							callExportData={callExportDataAssets}
							resetCallExportData={() => setCallExportAssets(false)}
						/>
					);
				case 'portfolio':
					return (
						<VInvestExpertisePortfolio
							listFunds={allFunds ?? []}
							portfolioFunds={resultData && resultData.portfolioFunds ? resultData.portfolioFunds : null}
							portfolioView={queryParams.portfolioView}
							callExportData={callExportData}
							resetCallExportData={() => setCallExportData(false)}
							typeTag={investExpertise.tag}
						/>
					);
				case 'history':
					return (
						<VInvestExpertiseHistory
							fundType={investExpertise}
							listFunds={allFunds ?? []}
							filters={filters}
							requestFilters={requestFilters}
							callExportData={callExportDataHistory}
							resetCallExportData={() => setCallExportDataHistory(false)}
						/>
					);
				case 'synthesis':
				default:
					return (
						<VInvestExpertiseSynthesis
							investExpertise={investExpertise}
							allFunds={allFunds ?? []}
							resultData={resultData}
							currency={currency as TInvestFundCurrency}
							callExportData={callExportDataListedAssets}
							resetCallExportData={() => setCallExportListedAssets(false)}
						/>
					);
			}
		} else {
			return <></>;
		}
	};

	return (
		<>
			{investExpertise && defaultRealizationStatus !== undefined ? (
				<>
					{allFunds && allFunds.length === 0 ? (
						<>
							<AppError404 variant="container" content={'No fund for ' + t(investExpertise.name)} />
						</>
					) : allFunds && allFunds.length > 0 ? (
						<>
							<Helmet>
								<title>
									{authContext.authContext === 'employee' ? '[E] ' : ''}{' '}
									{investExpertise ? t(investExpertise.name) : ''} -{' '}
									{queryParams.view === 'synthesis'
										? t(investmentTypeOngletLang.synthesis.tag)
										: queryParams.view === 'portfolio'
										? t(investmentTypeOngletLang.funds_portfolio.tag)
										: queryParams.view === 'assets'
										? t(investmentTypeOngletLang.funds_assets.tag)
										: queryParams.view === 'history'
										? t(investmentTypeOngletLang.history.tag)
										: queryParams.view}{' '}
									- {authContext.currentCompany ? authContext.currentCompany.name : ''}
								</title>
							</Helmet>
							<CAppTitle
								title={investExpertise ? t(investExpertise.name) : null}
								subtitle={
									investExpertise.tag !== 'listed_assets' && (
										<>
											<CDateUpdatedAtom
												isEstimated={resultData?.isEstimated}
												date={resultData?.dataUpdatedOn}
											/>{' '}
											{resultData?.kpisByFundForSiennaGestion?.some((f) =>
												allFunds.map((x) => x.id).includes(f.fund?.id ?? -1),
											) ? (
												<CInfoKpi />
											) : (
												<></>
											)}
										</>
									)
								}
							/>
							<FInvestSelectCurrencySwitch funds={allFundsForTypeTag ?? []} />
							<Box sx={{ marginTop: '30px' }}>
								<CFilters
									expertiseTag={investExpertise.tag}
									allFunds={allFunds}
									filters={filters}
									onChange={(newFilters) => {
										setFilters(newFilters);
									}}
									onUpdate={(filterFunds, reportingPeriod, investorIds) =>
										handleRequest(filterFunds, reportingPeriod, investorIds)
									}
								/>
							</Box>

							{!isFetchingResult ? (
								<>
									<VInvestFilteredFundsChips
										expertiseTag={investExpertise.tag}
										outOf={allFunds.length}
										filterFunds={[
											...(resultData?.kpisByFund?.map((x) => x.fund) ?? []),
											...(investExpertise.tag === 'listed_assets'
												? resultData?.kpisByFundForSiennaGestion?.map((x) => x.fund) ?? []
												: []),
										].filter((f): f is IFund => !!f)}
									/>
									<Box style={{ marginBottom: '20px', position: 'relative' }}>
										<Tabs
											value={queryParams.view ?? 'synthesis'}
											onChange={handleSelectOnglet}
											variant="scrollable"
											allowScrollButtonsMobile
											scrollButtons={isMobile ? true : false}
										>
											<Tab
												id="expertise-tabs-synthesis"
												className="expertise-tabs"
												value="synthesis"
												disabled={!investExpertise.expertiseOnglets.includes('synthesis')}
												sx={{
													display: !investExpertise.expertiseOnglets.includes('synthesis')
														? 'none'
														: '',
												}}
												icon={<DashboardOutlined />}
												iconPosition="start"
												label={t(investmentTypeOngletLang.synthesis.tag)}
											/>

											<Tab
												id="expertise-tabs-portfolio"
												className="expertise-tabs"
												value="portfolio"
												disabled={!investExpertise.expertiseOnglets.includes('portfolio')}
												sx={{
													display: !investExpertise.expertiseOnglets.includes('portfolio')
														? 'none'
														: '',
												}}
												icon={<AppCustomIcon tag="funds_portfolio" />}
												iconPosition="start"
												label={t(investmentTypeOngletLang.funds_portfolio.tag)}
											/>

											<Tab
												id="expertise-tabs-assets"
												className="expertise-tabs"
												value="assets"
												disabled={!investExpertise.expertiseOnglets.includes('assets')}
												sx={{
													display: !investExpertise.expertiseOnglets.includes('assets')
														? 'none'
														: '',
												}}
												icon={<AppCustomIcon tag="fund_assets" />}
												iconPosition="start"
												label={t(investmentTypeOngletLang.funds_assets.tag)}
											/>

											<Tab
												id="expertise-tabs-history"
												className="expertise-tabs"
												value="history"
												disabled={!investExpertise.expertiseOnglets.includes('history')}
												sx={{
													display: !investExpertise.expertiseOnglets.includes('history')
														? 'none'
														: '',
												}}
												icon={<AppCustomIcon tag="history" />}
												iconPosition="start"
												label={t(investmentTypeOngletLang.history.tag)}
											/>
										</Tabs>
										{queryParams.view === 'portfolio' &&
										investExpertise.expertiseOnglets.includes('portfolio') ? (
											<Box
												id="expertise-right-bar-portfolio"
												sx={{
													position: { xs: 'relative', sm: 'absolute' },
													textAlign: 'center',
													top: '0px',
													right: '0px',
													padding: { xs: '20px 0px 0px 0px', sm: '0px' },
												}}
											>
												<Stack
													direction="row"
													alignItems="center"
													justifyContent="center"
													spacing={3}
												>
													<Stack
														direction="row"
														alignItems="center"
														justifyContent="center"
														spacing={1}
													>
														<Box>{t(investmentActionLang.switch_fund.tag)}</Box>
														<CAppSwitch
															color="secondary"
															checked={queryParams.portfolioView === 'funds'}
															onClick={() => handleSwitchFundsSubportfolio()}
														/>
														<Box>{t(investmentActionLang.switch_subportfolio.tag)}</Box>
													</Stack>
													<Button
														variant="outlined"
														size="small"
														startIcon={<AppCustomIcon tag="export_data" />}
														color="primary"
														onClick={() => setCallExportData(true)}
													>
														{t(investmentActionLang.export_data.tag)}
													</Button>
												</Stack>
											</Box>
										) : (
											''
										)}
										{queryParams.view === 'history' &&
										investExpertise.expertiseOnglets.includes('history') ? (
											<Box
												id="expertise-right-bar-history"
												sx={{
													position: { xs: 'relative', sm: 'absolute' },
													textAlign: 'center',
													top: '0px',
													right: '0px',
													padding: { xs: '20px 0px 0px 0px', sm: '0px' },
												}}
											>
												<Button
													variant="outlined"
													size="small"
													startIcon={<AppCustomIcon tag="export_data" />}
													color="primary"
													onClick={() => setCallExportDataHistory(true)}
												>
													{t(investmentActionLang.export_data.tag)}
												</Button>
											</Box>
										) : (
											''
										)}
										{queryParams.view === 'assets' &&
										investExpertise.tag !== 'listed_assets' &&
										investExpertise.expertiseOnglets.includes('assets') ? (
											<Box
												id="expertise-right-bar-assets"
												sx={{
													position: { xs: 'relative', sm: 'absolute' },
													textAlign: 'center',
													top: '0px',
													right: '0px',
													padding: { xs: '20px 0px 0px 0px', sm: '0px' },
												}}
											>
												<Button
													variant="outlined"
													size="small"
													startIcon={<AppCustomIcon tag="export_data" />}
													color="primary"
													onClick={() => setCallExportAssets(true)}
												>
													{t(investmentActionLang.export_data.tag)}
												</Button>
											</Box>
										) : (
											''
										)}
										{queryParams.view === 'synthesis' &&
											investExpertise.tag === 'listed_assets' &&
											investExpertise.expertiseOnglets.includes('synthesis') && (
												<Box
													id="expertise-right-bar-listed_assets"
													sx={{
														position: { xs: 'relative', sm: 'absolute' },
														textAlign: 'center',
														top: '0px',
														right: '0px',
														padding: { xs: '20px 0px 0px 0px', sm: '0px' },
													}}
												>
													<Button
														variant="contained"
														size="small"
														color="secondary"
														startIcon={<AppCustomIcon tag={sort} />}
														onClick={() => setSort(sort === 'asc' ? 'desc' : 'asc')}
														sx={{ marginRight: '10px' }}
													>
														{t(investmentActionLang.position.tag)}
													</Button>
													<Button
														variant="outlined"
														size="small"
														startIcon={<AppCustomIcon tag="export_data" />}
														color="primary"
														onClick={() => setCallExportListedAssets(true)}
													>
														{t(investmentActionLang.export_data.tag)}
													</Button>
												</Box>
											)}
									</Box>
									<Box>{renderPanel()}</Box>
								</>
							) : (
								<Box sx={{ textAlign: 'center' }}>
									<CircularProgress />
								</Box>
							)}
							<CInvestDisclaimer />
						</>
					) : (
						<AppLoaderContent />
					)}
				</>
			) : investExpertise === undefined ? (
				<AppError404 variant="container" />
			) : (
				<AppLoaderContent />
			)}
		</>
	);
};

export default PInvestExpertise;
